// Bundle splitting (chore/frontend-bundle-split):
// Routes via React.lazy() — cada página vira um chunk próprio que carrega
// sob demanda. Antes: 712KB JS num único bundle. Depois: ~150KB inicial +
// 5-30KB por página visitada. Crítico pra iOS/4G onde a primeira carga
// vinha cara.
//
// Fallback: <PageLoader/> mostra spinner enquanto chunk baixa. Em rede
// rápida o flicker é imperceptível; em 3G mostra ~300ms até o JS chegar.
import { lazy, Suspense } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider } from "@/hooks/useAuth"
import { WorkspaceProvider } from "@/hooks/useWorkspace"
import { BiometricLockProvider } from "@/hooks/useBiometricLock"
import { BiometricLockGate } from "@/components/BiometricLockGate"
import { ProtectedRoute } from "@/components/ProtectedRoute"

// Login/Register: lazy também — primeira visita pra logged-out user paga
// 1 round-trip, mas o vendor chunk dispara em paralelo.
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })))
const Register = lazy(() =>
  import("@/pages/Register").then((m) => ({ default: m.Register }))
)
const Dashboard = lazy(() =>
  import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard }))
)
const Accounts = lazy(() =>
  import("@/pages/Accounts").then((m) => ({ default: m.Accounts }))
)
const Cards = lazy(() => import("@/pages/Cards").then((m) => ({ default: m.Cards })))
const Transactions = lazy(() =>
  import("@/pages/Transactions").then((m) => ({ default: m.Transactions }))
)
const Budgets = lazy(() => import("@/pages/Budgets").then((m) => ({ default: m.Budgets })))
const Advisor = lazy(() => import("@/pages/Advisor").then((m) => ({ default: m.Advisor })))
const Goals = lazy(() => import("@/pages/Goals").then((m) => ({ default: m.Goals })))
const CategoryDrillDown = lazy(() =>
  import("@/pages/CategoryDrillDown").then((m) => ({ default: m.CategoryDrillDown }))
)
const Calendar = lazy(() =>
  import("@/pages/Calendar").then((m) => ({ default: m.Calendar }))
)
const Inbox = lazy(() => import("@/pages/Inbox").then((m) => ({ default: m.Inbox })))
const Plan = lazy(() => import("@/pages/Plan").then((m) => ({ default: m.Plan })))
const Insights = lazy(() =>
  import("@/pages/Insights").then((m) => ({ default: m.Insights }))
)
const Recurring = lazy(() =>
  import("@/pages/Recurring").then((m) => ({ default: m.Recurring }))
)
const CardDetail = lazy(() =>
  import("@/pages/CardDetail").then((m) => ({ default: m.CardDetail }))
)
const AllInstallments = lazy(() =>
  import("@/pages/AllInstallments").then((m) => ({ default: m.AllInstallments }))
)
const Workspaces = lazy(() =>
  import("@/pages/Workspaces").then((m) => ({ default: m.Workspaces }))
)
const Categories = lazy(() =>
  import("@/pages/Categories").then((m) => ({ default: m.Categories }))
)
const Reports = lazy(() => import("@/pages/Reports").then((m) => ({ default: m.Reports })))
const Loans = lazy(() => import("@/pages/Loans").then((m) => ({ default: m.Loans })))
const Subscriptions = lazy(() =>
  import("@/pages/Subscriptions").then((m) => ({ default: m.Subscriptions }))
)
const CashFlow = lazy(() =>
  import("@/pages/CashFlow").then((m) => ({ default: m.CashFlow }))
)
const Rules = lazy(() => import("@/pages/Rules").then((m) => ({ default: m.Rules })))
const Investments = lazy(() =>
  import("@/pages/Investments").then((m) => ({ default: m.Investments }))
)

function PageLoader() {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-muted">
        <div className="w-8 h-8 rounded-full border-2 border-border border-t-accent animate-spin" />
        <span className="text-xs">Carregando…</span>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BiometricLockProvider>
      <WorkspaceProvider>
        <BiometricLockGate>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <Accounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cards"
              element={
                <ProtectedRoute>
                  <Cards />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budgets"
              element={
                <ProtectedRoute>
                  <Budgets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/advisor"
              element={
                <ProtectedRoute>
                  <Advisor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <Goals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/:id"
              element={
                <ProtectedRoute>
                  <CategoryDrillDown />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <Inbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plan"
              element={
                <ProtectedRoute>
                  <Plan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute>
                  <Insights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recurring"
              element={
                <ProtectedRoute>
                  <Recurring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cards/:id"
              element={
                <ProtectedRoute>
                  <CardDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/installments"
              element={
                <ProtectedRoute>
                  <AllInstallments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces"
              element={
                <ProtectedRoute>
                  <Workspaces />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute>
                  <Categories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/loans"
              element={
                <ProtectedRoute>
                  <Loans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cashflow"
              element={
                <ProtectedRoute>
                  <CashFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rules"
              element={
                <ProtectedRoute>
                  <Rules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/investments"
              element={
                <ProtectedRoute>
                  <Investments />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </BiometricLockGate>
      </WorkspaceProvider>
      </BiometricLockProvider>
    </AuthProvider>
  )
}
